import { Observable } from 'rxjs';
import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { FilterItem, OrderBy, PaginatedResponse } from '../_models/paginatedResponse';
import { FieldActivity, FieldActivityGrouped } from '../_models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FieldActivityGroupedService {
  private http = inject(HttpClient);

  private apiBaseUrlV1 = environment.apiBaseUrl + 'api/private/v1';
  private apiBaseUrlV2 = environment.apiBaseUrl + 'api/private/v2';

  private lastPagedResult = signal<PaginatedResponse<FieldActivityGrouped> | null>(null);
  private lastItemResult = signal<FieldActivityGrouped | null>(null);

  lastPagedResultSignal = this.lastPagedResult.asReadonly();
  lastItemResultSignal = this.lastItemResult.asReadonly();

  /**
   * Fetches paginated field activities grouped by activity with optional filters, sorting and search.
   *
   * @param params.number Page number starting from 1!
   * @param params.max Number of items per page
   * @param params.textSearch Free text search
   * @param params.year Filter activities by year
   * @param params.doneFilter Filter activities by done status
   * @param params.filterToApply Additional filters to apply
   * @param params.orderBy Sorting configuration
   * @returns Observable with paginated response
   */

  getFieldActivitiesGroupedPaged(params: {
    number?: number;
    max?: number;
    textSearch?: string;
    year?: number;
    doneFilter?: boolean | null;
    filterToApply?: FilterItem[];
    orderBy?: OrderBy[];
  }): Observable<PaginatedResponse<FieldActivityGrouped>> {
    const companyId = JSON.parse(localStorage.getItem('companySelected') ?? 'null')?.id ?? null;

    const filtersList: FilterItem[] = [
      ...(params.filterToApply ?? []),
      ...(params.doneFilter != null ? [{ name: 'done', value: params.doneFilter }] : []),
      ...(params.year != null ? [{ name: 'year', value: params.year }] : []),
      { name: 'companyId', value: companyId },
    ];

    const payload = {
      ...(params.number != null && { number: params.number }),
      ...(params.max != null && { max: params.max }),
      ...(params.textSearch?.trim() && { textSearch: params.textSearch.trim() }),
      sort: {
        orderBy: params.orderBy ?? [{ property: 'activityDate', direction: 'DESC' }],
      },
      filter: {
        filters: filtersList,
      },
    };

    return this.http
      .post<
        PaginatedResponse<FieldActivityGrouped>
      >(`${this.apiBaseUrlV2}/fieldActivityGroup`, payload)
      .pipe(
        tap((result) => this.lastPagedResult.set(result)),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  getFieldActivityGroupedByUUID(uuid: string): Observable<FieldActivityGrouped> {
    const url = `${this.apiBaseUrlV1}/fieldActivityGrouped/${uuid}`;

    return this.http.get<FieldActivityGrouped>(url).pipe(
      tap((result) => this.lastItemResult.set(result)),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  // Same as above but as promise
  getFieldActivityGroupedByUUIDPromise(uuid: string): Promise<FieldActivityGrouped> {
    return firstValueFrom(this.getFieldActivityGroupedByUUID(uuid));
  }

  cleanNonUsefulDataByFAGrouped(
    fieldActivityGroupedList: FieldActivityGrouped[]
  ): FieldActivityGrouped[] {
    fieldActivityGroupedList.forEach((grouped) => {
      grouped.activities.forEach((activity) => {
        // Clean mixes fieldActivity reference
        activity.mixes?.forEach((mix) => {
          if (mix?.fieldActivity?.id) {
            mix.fieldActivity = { id: mix.fieldActivity.id } as FieldActivity;
          }
        });

        // Clean fieldActivityItems fieldActivity reference
        if (activity.fieldActivityItems?.length) {
          activity.fieldActivityItems = activity.fieldActivityItems.map((item) => {
            if (item?.fieldActivity?.id) {
              return {
                ...item,
                fieldActivity: { id: item.fieldActivity.id } as FieldActivity,
              };
            }
            return item;
          });
        }
      });
    });

    return fieldActivityGroupedList;
  }
}
